import { SOSSIETY_RED, SOSSIETY_RED_HOVER } from '@/styles/brand';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { ArrowLeft, Check, X, Camera } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useEditProfile } from '@/features/profile/hooks/useEditProfile';
import { useTechniques, getCategoryName, getTechniqueName, sortTechniqueCategories } from '@/shared/hooks/useTechniques';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { ProfessionAutocomplete } from '@/components/ui/ProfessionAutocomplete';
import { BusinessTypeAutocomplete } from '@/components/ui/BusinessTypeAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ThemedSelectTrigger, ThemedSelectContent, ThemedSelectItem } from '@/components/ui/form/ThemedSelect';
import { supabase } from '@/integrations/supabase/client';
import { countries, getStatesByCountry, countriesWithStates } from '@/utils/locationData';
import { INPUT_THEMES, INPUT_STYLE_TOKENS } from '@/components/ui/form/inputThemes';
import { getInitials } from '@/utils/avatarUtils';

// ─── Design tokens (will move to design system) ───────────────────────────────
const COLORS = {
  bg:            '#160C0C',
  surface:       'rgba(255,255,255,0.04)',
  border:        'rgba(255,255,255,0.08)',
  accent:        SOSSIETY_RED,
  text:          '#FFFFFF',
  textSecondary: '#8D8D8D',
  inputBg:       'rgba(255,255,255,0.06)',
  inputBorder:   'rgba(255,255,255,0.15)',
  success:       '#22C55E',
  error:         '#EF4444',
};

const BELT_OPTIONS = [
  { value: 'white',  labelKey: 'profile.white_belt' },
  { value: 'blue',   labelKey: 'profile.blue_belt' },
  { value: 'purple', labelKey: 'profile.purple_belt' },
  { value: 'brown',  labelKey: 'profile.brown_belt' },
  { value: 'black',  labelKey: 'profile.black_belt' },
];

// ─── Small reusable field wrapper ────────────────────────────────────────────
const Field = ({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium" style={{ color: COLORS.textSecondary }}>
      {label}
    </label>
    {children}
    {error && <p className="text-xs" style={{ color: COLORS.error }}>{error}</p>}
  </div>
);

// ─── Shared input visual spec ─────────────────────────────────────────────────
// Sourced from design-system tokens — all fields in this form use these classes.
const INPUT_CLS = INPUT_THEMES.dark.input;

// ─── Dark-themed text input ───────────────────────────────────────────────────
const DarkInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ style, ...props }, ref) => (
  <Input
    ref={ref}
    {...props}
    className={INPUT_CLS}
    style={{
      fontSize: INPUT_STYLE_TOKENS.dark.fontSize,
      ...style,
    }}
  />
));
DarkInput.displayName = 'DarkInput';



// ─── Dark-themed select trigger (wraps Shadcn SelectTrigger) ─────────────────
// Uses design-system tokens + targets Radix's data-placeholder span.
const DarkSelectTrigger = (
  props: React.ComponentPropsWithoutRef<typeof SelectTrigger>
) => {
  const { className, ...rest } = props;
  return (
    <SelectTrigger
      {...rest}
      className={[
        INPUT_THEMES.dark.input,
        '[&_[data-placeholder]]:text-white/60',
        className ?? '',
      ].join(' ')}
    />
  );
};

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ label, id }: { label: string; id?: string }) => (
  <p
    id={id}
    className="text-xs font-semibold tracking-widest mt-6 mb-2 px-5 scroll-mt-24"
    style={{ color: COLORS.textSecondary }}
  >
    {label}
  </p>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const EditProfile = () => {
  const navigate = useNavigate();
  const { t, currentLanguage } = useI18n();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const {
    form,
    isLoading,
    isSaving,
    canSave,
    profilePictureUrl,
    usernameStatus,
    handleUsernameChange,
    selectedInterests,
    setSelectedInterests,
    selectedTechniques,
    setSelectedTechniques,
    showInstructorConfirm,
    pendingInstructorValue,
    handleInstructorToggle,
    confirmInstructorChange,
    cancelInstructorChange,
    showProDowngradeDialog,
    isCancelingPro,
    confirmDowngradeAndCancelPro,
    confirmDowngradeKeepPro,
    dismissProDowngradeDialog,
    uploadPhoto,
    photoState,
    onSubmit,
  } = useEditProfile();

  const { register, control, watch, setValue, formState: { errors } } = form;

  const watchCountry   = watch('country');
  const watchProfession = watch('profession');
  const watchIsInstructor = watch('is_instructor');
  const watchUsername  = watch('username');
  const watchBelt      = watch('belt_rank');

  const showStateCity = countriesWithStates.includes(watchCountry ?? '');
  const isEntrepreneur = (watchProfession ?? '').toLowerCase().includes('entrepreneur');

  // ─── Interests data ────────────────────────────────────────────────────────
  const [allInterests, setAllInterests] = useState<string[]>([]);
  const [interestSearch, setInterestSearch] = useState('');

  useEffect(() => {
    supabase
      .from('hobbies')
      .select('name')
      .order('name')
      .then(({ data }) => {
        if (data) setAllInterests(data.map((r: any) => r.name));
      });
  }, []);

  const filteredInterests = allInterests.filter(
    i => i.toLowerCase().includes(interestSearch.toLowerCase()) && !selectedInterests.includes(i)
  );

  // ─── Scroll to hash section on mount (deep link from CTAs) ────────────────
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || isLoading) return;
    // Wait for form to render before scrolling
    const timer = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // ─── Techniques data ───────────────────────────────────────────────────────
  const { data: rawCategories } = useTechniques();
  const techniqueCategories = rawCategories ? sortTechniqueCategories(rawCategories) : [];
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const activeCategory = techniqueCategories[activeCategoryIndex];
  const localSelected = activeCategory ? (selectedTechniques[activeCategory.name] || []) : [];

  const handleTechniqueToggle = (techniqueName: string) => {
    if (!activeCategory) return;
    const catName = activeCategory.name;
    const current = selectedTechniques[catName] || [];
    const next = current.includes(techniqueName)
      ? current.filter(t => t !== techniqueName)
      : current.length < 3 ? [...current, techniqueName] : current;
    setSelectedTechniques({ ...selectedTechniques, [catName]: next });
  };

  // ─── Photo upload handler ──────────────────────────────────────────────────
  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    const result = await uploadPhoto(file);
    if (result?.success && result.url) {
      setPreviewUrl(result.url);
    } else if (!result?.success) {
      setPreviewUrl(null);
    }
    // Defer revocation so React renders with the new URL before the blob is invalidated
    setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
  };

  if (isLoading) {
    return (
      <>
        <TopNavigation />
        <div className="min-h-screen w-full pt-16 flex items-center justify-center" style={{ backgroundColor: COLORS.bg }}>
          <div className="text-white/50 text-sm">{t('common.loading')}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <TopNavigation />

      <div className="min-h-screen w-full pb-36" style={{ backgroundColor: COLORS.bg }}>
        <div className="pt-16">
          {/* Header */}
          <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
            <button
              onClick={() => navigate(-1)}
              className="p-1 text-white/80 hover:text-white transition-colors z-10"
              aria-label={t('common.back')}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]" style={{ color: COLORS.text }}>
              {t('settings.edit_profile.title')}
            </h1>
          </div>
        </div>

        <form id="edit-profile-form" onSubmit={onSubmit}>

          {/* ─── Photo ─────────────────────────────────────────────────────── */}
          <div className="flex flex-col items-center pt-6 pb-8 gap-3">
            <div className="relative inline-block">
              <label className="cursor-pointer" aria-label={t('settings.edit_profile.change_photo')}>
                <input
                  id="profile-photo-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                  disabled={photoState.isUploading}
                />
                <div className="relative">
                  {photoState.isUploading && (
                    <div className="absolute inset-0 z-30 flex items-center justify-center rounded-full bg-black/50">
                      <span className="text-white text-xs font-semibold">{photoState.progress}%</span>
                    </div>
                  )}
                  <FramedAvatar
                    src={previewUrl ?? profilePictureUrl ?? undefined}
                    alt={watch('full_name')}
                    initials={getInitials(watch('full_name'), '?')}
                    beltRank={watchBelt}
                    size={64}
                    frameSize={{ w: 76, h: 84 }}
                    showFrame
                    isManual={false}
                  />
                </div>
              </label>
              {/* Camera button - always visible or on hover */}
              <button
                type="button"
                onClick={() => document.getElementById('profile-photo-input')?.click()}
                className="absolute -bottom-2 -right-2 w-7 h-7 bg-sossiety-red text-white rounded-full flex items-center justify-center hover:bg-sossiety-red-hover transition-colors z-50 shadow-lg border-2 border-white"
                disabled={photoState.isUploading}
                title={t('settings.edit_profile.change_photo')}
              >
                <Camera className="w-3.5 h-3.5" />
              </button>
            </div>
            {photoState.isUploading ? (
              <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                {t('settings.edit_profile.uploading_photo')} {photoState.progress}%
              </p>
            ) : previewUrl ? (
              <p className="text-xs text-green-400">
                {t('settings.edit_profile.photo_updated')}
              </p>
            ) : null}
          </div>

          {/* ─── Basic Info ─────────────────────────────────────────────────── */}
          <SectionHeader label={t('settings.edit_profile.section_basic')} id="section-academy" />
          <div className="px-5 flex flex-col gap-4">

            <Field label={t('profile.full_name')} error={errors.full_name?.message}>
              <DarkInput
                {...register('full_name')}
                placeholder={t('profile.full_name')}
              />
            </Field>

            <Field label={t('profile.username')} error={errors.username?.message}>
              <DarkInput
                value={watchUsername}
                onChange={e => handleUsernameChange(e.target.value)}
                placeholder="@username"
                maxLength={32}
              />
              {usernameStatus === 'checking' && (
                <p className="text-xs" style={{ color: COLORS.textSecondary }}>Checking…</p>
              )}
              {usernameStatus === 'available' && (
                <p className="text-xs flex items-center gap-1" style={{ color: COLORS.success }}>
                  <Check className="w-3 h-3" /> {t('profile.username_available')}
                </p>
              )}
              {(usernameStatus === 'unavailable' || usernameStatus === 'invalid') && (
                <p className="text-xs" style={{ color: COLORS.error }}>
                  {usernameStatus === 'unavailable'
                    ? t('profile.username_unavailable')
                    : t('profile.username_invalid')}
                </p>
              )}
            </Field>

<Field label={t('profile.add_your_jiu_jitsu_school')}>
              <Controller
                control={control}
                name="main_academy_name"
                render={({ field }) => (
                  <AcademyAutocomplete
                    value={field.value ? { id: watch('main_academy_id') || '', name: field.value } : ''}
                    onChange={academy => {
                      if (academy && typeof academy === 'object') {
                        setValue('main_academy_id', academy.id, { shouldDirty: true });
                        field.onChange(academy.name);
                      } else {
                        setValue('main_academy_id', null, { shouldDirty: true });
                        field.onChange('');
                      }
                    }}
                    inputStyle={{
                      backgroundColor: COLORS.inputBg,
                      borderColor: COLORS.inputBorder,
                    }}
                  />
                )}
              />
            </Field>
          </div>

          {/* ─── Account Type ───────────────────────────────────────────────── */}
          <SectionHeader label={t('settings.edit_profile.section_account_type')} />
          <div className="px-5">
            <button
              type="button"
              className="flex items-center justify-between w-full py-3.5 px-4 rounded-lg"
              style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}
              onClick={() => handleInstructorToggle(!watchIsInstructor)}
            >
              <div>
                <p className="text-sm font-medium text-left" style={{ color: COLORS.text }}>
                  {watchIsInstructor ? t('profile.instructor') : t('profile.practitioner')}
                </p>
                <p className="text-xs text-left mt-0.5" style={{ color: COLORS.textSecondary }}>
                  {watchIsInstructor
                    ? t('settings.edit_profile.currently_instructor')
                    : t('settings.edit_profile.currently_practitioner')}
                </p>
              </div>
              <span
                className="text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{
                  backgroundColor: watchIsInstructor ? COLORS.accent : 'rgba(255,255,255,0.1)',
                  color: COLORS.text,
                }}
              >
                {watchIsInstructor ? t('profile.instructor') : t('profile.practitioner')}
              </span>
            </button>
          </div>

          {/* ─── Location ───────────────────────────────────────────────────── */}
          <SectionHeader label={t('settings.edit_profile.section_location')} id="section-location" />
          <div className="px-5 flex flex-col gap-4">

            <Field label={t('profile.country')} error={errors.country?.message}>
              <Controller
                control={control}
                name="country"
                render={({ field }) => (
                  <Select
                    value={field.value || ''}
                    onValueChange={v => {
                      field.onChange(v);
                      setValue('state', '', { shouldDirty: true });
                      setValue('city', '', { shouldDirty: true });
                    }}
                  >
                    <ThemedSelectTrigger theme="dark">
                      <SelectValue placeholder={t('profile.select_country')} />
                    </ThemedSelectTrigger>
                    <ThemedSelectContent theme="dark">
                      {countries.map(c => (
                        <ThemedSelectItem key={c.value} value={c.value} theme="dark">
                          {c.label}
                        </ThemedSelectItem>
                      ))}
                    </ThemedSelectContent>
                  </Select>
                )}
              />
            </Field>

            {showStateCity && (
              <>
                <Field label={t('profile.state')} error={errors.state?.message}>
                  <Controller
                    control={control}
                    name="state"
                    render={({ field }) => (
                      <Select value={field.value || ''} onValueChange={field.onChange}>
                        <ThemedSelectTrigger theme="dark">
                          <SelectValue placeholder={t('profile.select_state')} />
                        </ThemedSelectTrigger>
                        <ThemedSelectContent theme="dark">
                          {getStatesByCountry(watchCountry ?? '').map(s => (
                            <ThemedSelectItem key={s.value} value={s.value} theme="dark">
                              {s.label}
                            </ThemedSelectItem>
                          ))}
                        </ThemedSelectContent>
                      </Select>
                    )}
                  />
                </Field>

                <Field label={t('profile.city')} error={errors.city?.message}>
                  <DarkInput
                    {...register('city')}
                    placeholder={t('profile.city')}
                  />
                </Field>
              </>
            )}
          </div>

          {/* ─── Profession ─────────────────────────────────────────────────── */}
          <SectionHeader label={t('settings.edit_profile.section_profession')} id="section-profession" />
          <div className="px-5 flex flex-col gap-4">

            <Field label={t('profile.profession')} error={errors.profession?.message}>
              <Controller
                control={control}
                name="profession"
                render={({ field }) => (
                  <ProfessionAutocomplete
                    value={field.value || ''}
                    onChange={v => {
                      field.onChange(v);
                      if (!v.toLowerCase().includes('entrepreneur')) {
                        setValue('business_type', '', { shouldDirty: true });
                      }
                    }}
                    theme="dark"
                  />
                )}
              />
            </Field>

            {isEntrepreneur && (
              <Field label={t('profile.business_type')} error={errors.business_type?.message}>
                <Controller
                  control={control}
                  name="business_type"
                  render={({ field }) => (
                    <BusinessTypeAutocomplete
                      value={field.value || ''}
                      onChange={field.onChange}
                      theme="dark"
                    />
                  )}
                />
              </Field>
            )}
          </div>

          {/* ─── Social Networks ────────────────────────────────────────────── */}
          <SectionHeader label={t('profile.social_networks')} />
          <div className="px-5 flex flex-col gap-4">

            <Field label={t('profile.instagram')}>
              <DarkInput
                {...register('instagram')}
                placeholder={t('onboarding.social.placeholder')}
              />
            </Field>

            <Field label={t('profile.x_twitter')}>
              <DarkInput
                {...register('x')}
                placeholder={t('onboarding.social.placeholder')}
              />
            </Field>

            <Field label={t('profile.facebook')}>
              <DarkInput
                {...register('facebook')}
                placeholder={t('onboarding.social.placeholder')}
              />
            </Field>

            <Field label={t('profile.youtube')}>
              <DarkInput
                {...register('youtube')}
                placeholder={t('onboarding.social.placeholder')}
              />
            </Field>

            <Field label={t('profile.website')}>
              <DarkInput
                {...register('website')}
                placeholder={t('profile.website_placeholder')}
              />
            </Field>

            <Field label={t('profile.linkedin')}>
              <DarkInput
                {...register('linkedin')}
                placeholder={t('profile.linkedin_placeholder')}
              />
            </Field>

          </div>

          {/* ─── Interests ──────────────────────────────────────────────────── */}
          <SectionHeader label={t('settings.edit_profile.section_interests')} id="section-interests" />
          <div className="px-5 flex flex-col gap-3">

            {/* Selected chips */}
            {selectedInterests.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedInterests.map(i => (
                  <span
                    key={i}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
                  >
                    {i}
                    <button
                      type="button"
                      onClick={() => setSelectedInterests(selectedInterests.filter(x => x !== i))}
                      aria-label={`Remove ${i}`}
                    >
                      <X className="w-3 h-3" style={{ color: COLORS.textSecondary }} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {selectedInterests.length < 5 && (
              <>
                <DarkInput
                  value={interestSearch}
                  onChange={e => setInterestSearch(e.target.value)}
                  placeholder={t('settings.edit_profile.search_interests')}
                />
                {interestSearch.length > 0 && filteredInterests.length > 0 && (
                  <div
                    className="rounded-lg border overflow-hidden"
                    style={{ backgroundColor: '#1E1212', borderColor: COLORS.border }}
                  >
                    {filteredInterests.slice(0, 6).map(i => (
                      <button
                        key={i}
                        type="button"
                        className="w-full text-left px-4 py-2.5 text-sm border-b last:border-b-0 hover:bg-white/5 transition-colors"
                        style={{ color: COLORS.text, borderColor: COLORS.border }}
                        onClick={() => {
                          setSelectedInterests([...selectedInterests, i]);
                          setInterestSearch('');
                        }}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            <p className="text-xs" style={{ color: COLORS.textSecondary }}>
              {selectedInterests.length}/5 {t('settings.edit_profile.interests_selected')}
            </p>
          </div>

          {/* ─── Favorite Techniques ────────────────────────────────────────── */}
          {techniqueCategories.length > 0 && (
            <>
              <SectionHeader label={t('settings.edit_profile.section_techniques')} />
              <div className="px-5 flex flex-col gap-4">

                {/* Category tabs — horizontal slider style */}
                <div className="flex gap-4 border-b overflow-x-auto scrollbar-none" style={{ borderColor: COLORS.border }}>
                  {techniqueCategories.map((cat, idx) => (
                    <button
                      key={cat.name}
                      type="button"
                      className="flex-shrink-0 px-1 py-2.5 text-xs font-medium transition-colors relative whitespace-nowrap"
                      style={{ color: idx === activeCategoryIndex ? COLORS.accent : COLORS.textSecondary }}
                      onClick={() => setActiveCategoryIndex(idx)}
                    >
                      {getCategoryName(cat, currentLanguage)}
                      {idx === activeCategoryIndex && (
                        <div
                          className="absolute bottom-0 left-0 right-0 h-0.5"
                          style={{ backgroundColor: COLORS.accent }}
                        />
                      )}
                    </button>
                  ))}
                </div>

                {/* Techniques grid — pill style */}
                {activeCategory && (
                  <div className="flex flex-wrap gap-2">
                    {activeCategory.techniques.map(tech => {
                      const isSelected = localSelected.includes(tech.name);
                      const isDisabled = !isSelected && localSelected.length >= 3;
                      return (
                        <button
                          key={tech.id}
                          type="button"
                          disabled={isDisabled}
                          className="rounded-full px-3 py-1 text-sm font-medium transition-colors duration-200 border"
                          style={{
                            backgroundColor: isSelected ? COLORS.accent : 'transparent',
                            borderColor: isSelected ? COLORS.accent : COLORS.text,
                            color: isSelected ? '#FFFFFF' : COLORS.text,
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                          }}
                          onClick={() => handleTechniqueToggle(tech.name)}
                        >
                          {getTechniqueName(tech, currentLanguage)}
                        </button>
                      );
                    })}
                  </div>
                )}

                <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                  {localSelected.length}/3 {t('settings.edit_profile.techniques_selected')}
                </p>
              </div>
            </>
          )}

        </form>
      </div>

      {/* ─── Fixed save footer ────────────────────────────────────────────── */}
      <div className="fixed bottom-0 inset-x-0 bg-gradient-to-t from-[#160C0C] to-transparent pt-10 pb-8 px-5 pointer-events-none">
        <Button
          type="submit"
          form="edit-profile-form"
          disabled={isSaving}
          className="w-full h-12 rounded-full font-semibold text-white pointer-events-auto"
          style={{ backgroundColor: COLORS.accent }}
        >
          {isSaving ? t('profile.saving') : t('common.save')}
        </Button>
      </div>

      {/* ─── Pro downgrade dialog (SOS-355 §2) ─────────────────────────────── */}
      {/* Instructor with active Pro switching to practitioner: force the user
          to consciously decide whether to cancel the subscription before
          changing role, so they don't keep being charged for a tier they no
          longer have UI access to. */}
      <AlertDialog open={showProDowngradeDialog} onOpenChange={(open) => { if (!open) dismissProDowngradeDialog(); }}>
        <AlertDialogContent
          className="border"
          style={{
            backgroundColor: '#2A1F1F',
            borderColor: 'rgba(255,255,255,0.1)',
            borderRadius: '12px',
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {t('settings.edit_profile.pro_downgrade_title')}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: COLORS.textSecondary }}>
              {t('settings.edit_profile.pro_downgrade_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex-col !justify-start !space-x-0 gap-2">
            <AlertDialogAction
              className="rounded-full border-0 w-full h-11 text-white mt-0"
              style={{ backgroundColor: COLORS.accent }}
              disabled={isCancelingPro}
              onClick={(e) => { e.preventDefault(); confirmDowngradeAndCancelPro(); }}
            >
              {isCancelingPro
                ? t('common.loading')
                : t('settings.edit_profile.pro_downgrade_cancel_cta')}
            </AlertDialogAction>
            <AlertDialogAction
              className="rounded-full border w-full h-11 text-white mt-0"
              style={{ backgroundColor: 'transparent', borderColor: 'rgba(255,255,255,0.2)' }}
              disabled={isCancelingPro}
              onClick={(e) => { e.preventDefault(); confirmDowngradeKeepPro(); }}
            >
              {t('settings.edit_profile.pro_downgrade_keep_cta')}
            </AlertDialogAction>
            <AlertDialogCancel
              className="mt-0 rounded-full border-0 w-full h-11 text-white"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
              disabled={isCancelingPro}
              onClick={dismissProDowngradeDialog}
            >
              {t('common.cancel')}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─── Instructor type confirmation dialog ───────────────────────────── */}
      <AlertDialog open={showInstructorConfirm} onOpenChange={cancelInstructorChange}>
        <AlertDialogContent
          className="border"
          style={{
            backgroundColor: '#2A1F1F',
            borderColor: 'rgba(255,255,255,0.1)',
            borderRadius: '12px',
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {pendingInstructorValue
                ? t('settings.edit_profile.become_instructor_title')
                : t('settings.edit_profile.become_practitioner_title')}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: COLORS.textSecondary }}>
              {pendingInstructorValue
                ? t('settings.edit_profile.become_instructor_description')
                : t('settings.edit_profile.become_practitioner_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel
              className="rounded-full border-0 flex-1 h-11 text-white"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              onClick={cancelInstructorChange}
            >
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full border-0 flex-1 h-11 text-white"
              style={{ backgroundColor: COLORS.accent }}
              onClick={confirmInstructorChange}
            >
              {t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditProfile;
